
// import "css"
import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios, { AxiosError } from "axios";
import {
  Eye,
  EyeOff,
  Loader2,
  User,
  Lock,
  LogIn,
  Sparkles,
  Shield,
} from "lucide-react";
import Swal from "sweetalert2";
import { getRoleId } from "Common/Components/getRoleId";

// تایپ مقادیر فرم
interface LoginFormValues {
  username: string;
  password: string;
}

// تایپ پاسخ موفقیت‌آمیز سرور (مطابق API خودت تنظیم کن)
interface LoginSuccessResponse {
  access_token: string;
  refresh_token: string;
}

// تایپ خطای ولیدیشن (۴۲۲) – باز هم بستگی به ساختار سرور داره
interface ValidationErrorResponse {
  message?: string;
  errors?: Record<string, string[]> | string;
}

const Login: React.FC = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("نام کاربری یا ایمیل ضروری است"),
      password: Yup.string().required("رمز عبور ضروری است"),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("username", values.username.trim());
        params.append("password", values.password.trim());
        // params.append("grant_type", "password");   // بعضی نسخه‌های FastAPI اجباری دارند

        const res = await axios.post<LoginSuccessResponse>(
          "https://rasad-api.havirard.ir/token",   // بدون اِسلَش آخر
          params,
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          }
        );
        console.log(res);
        localStorage.setItem("app_havir", res.data.access_token);
        localStorage.setItem("app_rt", res.data.refresh_token);
        localStorage.setItem("login_time", Date.now().toString());
        window.dispatchEvent(new Event("auth-change"));
        const roleID = getRoleId(res.data.access_token)
        console.log(roleID, "Ssssssss");
        if (roleID === 4) {
          navigate("/DriverTasks");
          Swal.fire("ورود موفق", "به پنل کاربری خود خوش امدید", "success");
        } else {
          Swal.fire("خوش آمدید", "ورود موفقیت‌آمیز بود", "success");
          navigate("/ManageOrders");
        }



      } catch (err) {
        const error = err as AxiosError<ValidationErrorResponse>;
        console.log(err);

        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;

          if (status === 422) {
            if (data?.errors) {
              const messages = Object.values(data.errors).flat().join("، ");
              toast.error(messages);
            } else if (data?.message) {
              toast.error(data.message);
            } else {
              toast.error("فرمت اطلاعات ارسالی نامعتبر است");
            }
          } else if (status === 401) {
            toast.error("نام کاربری یا رمز عبور اشتباه است");
          } else {
            toast.error("خطای سرور: " + (data?.message || "مشکل ناشناخته"));
          }
        } else {
          toast.error("خطا در ارتباط با سرور");
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <div
      style={{ direction: "rtl" }}
      className="relative  min-h-screen overflow-hidden bg-gradient-to-br from-blue-50 via-white to-sky-100 antialiased"
    >
      <div className="absolute inset-0 bg-white/40 backdrop-blur-[80px]" />
      <div className="absolute top-[-20%] left-[-10%] h-[80%] w-[80%] rounded-full bg-blue-200/40 blur-3xl" />
      <div className="absolute bottom-[-30%] right-[-10%] h-[90%] w-[90%] rounded-full bg-sky-200/30 blur-3xl" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 pb-8">
        <div className="w-full max-w-md">
          <div className="overflow-hidden rounded-2xl bg-white/80 shadow-2xl backdrop-blur-md">
            <div className="p-6 sm:p-8">
              <div className="flex justify-center mb-6">
                <div className="flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 text-sm text-blue-700">
                  <Sparkles className="h-3 w-3" />
                  <span>ورود به پنل</span>
                </div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-sky-500 shadow-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">پایش هوشمند آرد</h2>
                <p className="mt-2 text-sm text-gray-500">سامانه هوشمند مدیریت یکپارچه</p>
              </div>

              <form onSubmit={formik.handleSubmit} className="mt-8 space-y-5">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    نام کاربری یا ایمیل
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="username"
                      autoComplete="off"
                      dir="rtl"
                      className="block w-full rounded-xl border border-gray-200 bg-white/50 py-3 pr-11 pl-4 text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                      placeholder="نام کاربری یا ایمیل خود را وارد کنید"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.username}
                    />
                  </div>
                  {formik.touched.username && formik.errors.username && (
                    <p className="mt-1 text-xs text-red-500">{formik.errors.username}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    رمز عبور
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={passwordVisible ? "text" : "password"}
                      name="password"
                      autoComplete="off"
                      dir="rtl"
                      className="block w-full rounded-xl border border-gray-200 bg-white/50 py-3 pr-11 pl-12 text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                      placeholder="رمز عبور خود را وارد کنید"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {passwordVisible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-1 text-xs text-red-500">{formik.errors.password}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                    />
                    <span>مرا به خاطر بسپار</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => toast.info("جهت بازیابی رمز عبور با پشتیبانی تماس بگیرید")}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    رمز خود را فراموش کرده‌اید؟
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 py-3 font-bold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-blue-300/50 disabled:opacity-60"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>در حال ورود...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <LogIn className="h-5 w-5" />
                      <span>ورود به پایش آرد</span>
                    </div>
                  )}
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="rounded-full bg-white px-3 py-0.5 text-xs text-gray-500">
                      . ورود با شبکه‌های اجتماعی
                    </span>
                  </div>
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    className="rounded-full bg-gray-100 p-2.5 text-gray-600 transition hover:bg-blue-100 hover:text-blue-600"
                    onClick={() => toast.info("ادغام با گوگل در حال توسعه")}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="rounded-full bg-gray-100 p-2.5 text-gray-600 transition hover:bg-blue-100 hover:text-blue-600"
                    onClick={() => toast.info("ادغام با گیت‌هاب در حال توسعه")}
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.68-.22.68-.48 0-.24-.01-.88-.01-1.73-2.78.6-3.37-1.2-3.37-1.2-.45-1.15-1.11-1.46-1.11-1.46-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02.8-.22 1.65-.33 2.5-.33.85 0 1.7.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85 0 1.34-.01 2.42-.01 2.75 0 .27.18.58.69.48C19.13 20.17 22 16.42 22 12c0-5.52-4.48-10-10-10z" />
                    </svg>
                  </button>
                </div>

                <p className="text-center text-xs text-gray-400">© ۱۴۰۴-۱۴۰۵ - سامانه یکپارچه پایش هوشمند آرد</p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} rtl theme="colored" />
    </div>
  );
};

export default Login;